import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    sendOtpUrl: String,
    verifyOtpUrl: String,
    loginUrl: String
  }

  static targets = [
    "emailInput",
    "sendButton",
    "otpSection",
    "otpInput",
    "verifyButton",
    "resendButton",
    "passwordSection",
    "passwordInput",
    "passwordConfirmInput",
    "passwordMatch",
    "contactSection",
    "paymentSection",
    "submitSection",
    "statusMessage",
    "statusText",
    "statusLink",
    "verifiedBadge",
    "emailDisplay"
  ]

  connect() {
    this.resendTimer = null
  }

  disconnect() {
    if (this.resendTimer) clearInterval(this.resendTimer)
  }

  async sendOtp(event) {
    event.preventDefault()
    const email = this.emailInputTarget.value.trim()
    if (!email) return

    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.textContent = this.sendButtonTarget.dataset.sendingText || "Sending..."
    this.clearStatus()

    try {
      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (data.existing_member) {
        this.showStatus("info", data.message, this.loginUrlValue)
        this.sendButtonTarget.disabled = false
        this.sendButtonTarget.textContent = this.sendButtonTarget.dataset.defaultText || "Verify"
        return
      }

      if (response.ok && data.success) {
        this.emailInputTarget.readOnly = true
        this.emailInputTarget.classList.add("bg-gray-100")
        this.sendButtonTarget.classList.add("hidden")
        this.otpSectionTarget.classList.remove("hidden")
        this.otpInputTarget.focus()
        this.showStatus("success", data.message)
        this.startResendTimer()
      } else {
        this.showStatus("error", data.error || "Something went wrong.")
        this.sendButtonTarget.disabled = false
        this.sendButtonTarget.textContent = this.sendButtonTarget.dataset.defaultText || "Verify"
      }
    } catch {
      this.showStatus("error", "Something went wrong. Please try again.")
      this.sendButtonTarget.disabled = false
      this.sendButtonTarget.textContent = this.sendButtonTarget.dataset.defaultText || "Verify"
    }
  }

  async verifyCode(event) {
    event.preventDefault()
    const code = this.otpInputTarget.value.trim()
    if (code.length !== 6) return

    this.verifyButtonTarget.disabled = true
    this.verifyButtonTarget.textContent = this.verifyButtonTarget.dataset.verifyingText || "Verifying..."
    this.clearStatus()

    try {
      const response = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ code })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.otpSectionTarget.classList.add("hidden")
        this.verifiedBadgeTarget.classList.remove("hidden")

        if (this.hasEmailDisplayTarget) {
          this.emailDisplayTarget.textContent = data.email
        }

        this.passwordSectionTarget.classList.remove("hidden")
        this.passwordInputTarget.focus()
        this.showStatus("success", data.message || "Email verified!")

        if (this.resendTimer) clearInterval(this.resendTimer)
      } else {
        this.showStatus("error", data.error || "Invalid code.")
        this.verifyButtonTarget.disabled = false
        this.verifyButtonTarget.textContent = this.verifyButtonTarget.dataset.defaultText || "Verify Code"
      }
    } catch {
      this.showStatus("error", "Verification failed. Please try again.")
      this.verifyButtonTarget.disabled = false
      this.verifyButtonTarget.textContent = this.verifyButtonTarget.dataset.defaultText || "Verify Code"
    }
  }

  checkPasswords() {
    const pw = this.passwordInputTarget.value
    const pwConfirm = this.passwordConfirmInputTarget.value

    if (this.hasPasswordMatchTarget) {
      if (pw.length > 0 && pwConfirm.length > 0) {
        if (pw === pwConfirm && pw.length >= 8) {
          this.passwordMatchTarget.textContent = this.passwordMatchTarget.dataset.matchText || "Passwords match"
          this.passwordMatchTarget.className = "text-xs text-green-600 mt-1"
        } else if (pw !== pwConfirm) {
          this.passwordMatchTarget.textContent = this.passwordMatchTarget.dataset.noMatchText || "Passwords don't match"
          this.passwordMatchTarget.className = "text-xs text-red-600 mt-1"
        } else {
          this.passwordMatchTarget.textContent = this.passwordMatchTarget.dataset.tooShortText || "Password must be at least 8 characters"
          this.passwordMatchTarget.className = "text-xs text-red-600 mt-1"
        }
      } else {
        this.passwordMatchTarget.textContent = ""
      }
    }

    // Reveal contact and payment when passwords are valid
    if (pw.length >= 8 && pw === pwConfirm) {
      this.contactSectionTarget.classList.remove("hidden")
      this.paymentSectionTarget.classList.remove("hidden")
      this.submitSectionTarget.classList.remove("hidden")
    }
  }

  resendOtp(event) {
    this.emailInputTarget.readOnly = false
    this.emailInputTarget.classList.remove("bg-gray-100")
    this.sendOtp(event)
  }

  startResendTimer() {
    let seconds = 60
    this.resendButtonTarget.disabled = true

    if (this.resendTimer) clearInterval(this.resendTimer)

    this.resendTimer = setInterval(() => {
      seconds--
      this.resendButtonTarget.textContent = `${this.resendButtonTarget.dataset.resendText || "Resend code"} (${seconds}s)`

      if (seconds <= 0) {
        clearInterval(this.resendTimer)
        this.resendTimer = null
        this.resendButtonTarget.disabled = false
        this.resendButtonTarget.textContent = this.resendButtonTarget.dataset.resendText || "Resend code"
      }
    }, 1000)
  }

  showStatus(type, message, loginUrl = null) {
    if (!this.hasStatusMessageTarget) return

    const colors = {
      success: "bg-green-50 border-green-400 text-green-700",
      error: "bg-red-50 border-red-400 text-red-700",
      info: "bg-blue-50 border-blue-400 text-blue-700"
    }

    this.statusTextTarget.textContent = message

    if (this.hasStatusLinkTarget) {
      if (loginUrl) {
        this.statusLinkTarget.href = loginUrl
        this.statusLinkTarget.classList.remove("hidden")
      } else {
        this.statusLinkTarget.classList.add("hidden")
      }
    }

    this.statusMessageTarget.className = `p-4 border-l-4 rounded mb-4 ${colors[type] || colors.info}`
    this.statusMessageTarget.classList.remove("hidden")
  }

  clearStatus() {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.add("hidden")
    }
  }

  // Auto-advance OTP input when 6 digits entered
  otpInputChanged() {
    const code = this.otpInputTarget.value.replace(/\D/g, "").slice(0, 6)
    this.otpInputTarget.value = code

    if (code.length === 6) {
      this.verifyButtonTarget.click()
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
